import React from "react";

import { User } from "../../models/entities/user";
import { UserLevelDisplay } from "../../models/enums/UserLevelDisplay";

import { Table, Empty, Tooltip } from "antd";
import {
  Container,
  Button,
  ButtonContainer,
  InfoIcon,
  StatusSpan,
} from "./styles";

interface TwygoUsersProps {
  users: User[];
  loading: boolean;
  loadingCompanyUsers: boolean;
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: (keys: React.Key[]) => void;
  setActionType: (type: "send" | "inactivate" | "activate" | null) => void;
  companyUsers: { user_id: number; full_name: string }[];
  selectedCompany?: number;
  filteredUsers: User[];
  isActionLoading: (userId: number, action: string) => boolean;
  handleSendUserToTwygo: (userId: number) => Promise<void>;
  handleInactivateUser: (userId: number) => Promise<void>;
  handleRestoreUser: (userId: number) => Promise<void>;
  handleFetchUserCourses: (userId: number) => Promise<void>;
  profiles?: { id: number; name: string }[];
}

type ResponsiveBreakpoint = "xxl" | "xl" | "lg" | "md" | "sm" | "xs";
type ResponsiveArray = ResponsiveBreakpoint[];

const TwygoUsers: React.FC<TwygoUsersProps> = ({
  users,
  loading,
  loadingCompanyUsers,
  selectedRowKeys,
  setSelectedRowKeys,
  setActionType,
  companyUsers,
  selectedCompany,
  filteredUsers,
  isActionLoading,
  handleSendUserToTwygo,
  handleInactivateUser,
  handleRestoreUser,
  handleFetchUserCourses,
  profiles,
}) => {
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
    },

    {
      title: "Nome",
      dataIndex: "full_name",
      key: "full_name",
      width: "18%",
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Nível",
      dataIndex: "level",
      key: "level",
      width: "10%",
      ellipsis: true,
      responsive: ["lg"] as ResponsiveArray,
      render: (_: any, record: User) => {
        const levelName =
          UserLevelDisplay.find((level) => level.value === record.level)
            ?.label || "Nível não definido";
        return (
          <Tooltip title={levelName}>
            <span>{levelName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Cargo",
      dataIndex: "profile",
      key: "profile",
      width: "12%",
      ellipsis: true,
      render: (_: any, record: User) => {
        const userProfile = profiles?.find(
          (profile) => profile.id === record.profile
        );
        const profileName = userProfile?.name || "Sem cargo definido";
        return (
          <Tooltip title={profileName}>
            <span>{profileName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "12%",
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: (
        <span>
          Magic Link{" "}
          <Tooltip title="Link de acesso direto à plataforma Twygo. Quando configurado, o usuário pode acessar seus cursos sem precisar fazer login na plataforma da Twygo.">
            <InfoIcon />
          </Tooltip>
        </span>
      ),
      dataIndex: "magic_link_twygo",
      key: "magic_link_twygo",
      width: "12%",
      responsive: ["sm"] as ResponsiveArray,
      render: (_: any, record: User) => {
        return (
          <StatusSpan status={record.magic_link_twygo ? "success" : "error"}>
            {record.magic_link_twygo ? "Sim" : "Não"}
          </StatusSpan>
        );
      },
    },
    {
      title: "Status Twygo",
      key: "twygo_status",
      width: "12%",
      ellipsis: true,
      render: (_: any, record: User) => {
        if (!record.user_twygo_id) {
          return <StatusSpan status="info">Não cadastrado</StatusSpan>;
        }
        return (
          <StatusSpan status={record.user_twygo_status ? "success" : "error"}>
            {record.user_twygo_status ? "Ativo" : "Inativo"}
          </StatusSpan>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: "20%",
      render: (_: any, record: User) => (
        <>
          {!record.user_twygo_id ? (
            <Button
              actionType="send"
              type="primary"
              onClick={() => handleSendUserToTwygo(record?.id || 0)}
              loading={isActionLoading(record?.id || 0, "send")}
              style={{ marginRight: 8 }}
            >
              Enviar para Twygo
            </Button>
          ) : (
            <ButtonContainer>
              {record.user_twygo_status ? (
                <>
                  <Button
                    actionType="open_courses"
                    onClick={() =>
                      handleFetchUserCourses(record?.user_twygo_id || 0)
                    }
                    loading={isActionLoading(record?.id || 0, "courses")}
                  >
                    Ver Cursos
                  </Button>
                  <Button
                    actionType="inactivate"
                    onClick={() => handleInactivateUser(record?.id || 0)}
                    loading={isActionLoading(record?.id || 0, "inactivate")}
                  >
                    Inativar
                  </Button>
                </>
              ) : (
                <Button
                  actionType="activate"
                  onClick={() => handleRestoreUser(record?.id || 0)}
                  loading={isActionLoading(record?.id || 0, "restore")}
                >
                  Ativar
                </Button>
              )}
            </ButtonContainer>
          )}
        </>
      ),
    },
  ];

  return (
    <Container>
      <Table
        rowSelection={{
          selectedRowKeys,
          hideSelectAll: true,
          onChange: (selectedKeys, selectedRows) => {
            if (selectedKeys.length > 0 && selectedRowKeys.length === 0) {
              const firstUser = selectedRows[0];
              if (!firstUser.user_twygo_id) {
                setActionType("send");
              } else if (firstUser.user_twygo_status) {
                setActionType("inactivate");
              } else {
                setActionType("activate");
              }
            } else if (selectedKeys.length === 0) {
              setActionType(null);
            }
            setSelectedRowKeys(selectedKeys);
          },
          getCheckboxProps: (record) => {
            if (selectedRowKeys.length === 0) return {};

            const firstSelectedUser = users.find(
              (user) => user.id === selectedRowKeys[0]
            );
            if (!firstSelectedUser) return {};

            if (!firstSelectedUser.user_twygo_id) {
              return {
                disabled: record.user_twygo_id !== null,
                name: "checkboxDisabled",
              };
            }

            if (firstSelectedUser.user_twygo_status) {
              return {
                disabled: !record.user_twygo_id || !record.user_twygo_status,
                name: "checkboxDisabled",
              };
            }

            return {
              disabled: !record.user_twygo_id || record.user_twygo_status,
              name: "checkboxDisabled",
            };
          },
        }}
        columns={columns}
        dataSource={filteredUsers}
        rowKey="id"
        loading={loading || loadingCompanyUsers}
        scroll={{ y: 600 }}
        locale={{
          emptyText:
            selectedCompany !== undefined && companyUsers.length === 0 ? (
              <Empty description="Nenhum usuário encontrado para esta loja" />
            ) : (
              <Empty description="Nenhum usuário encontrado" />
            ),
        }}
      />
    </Container>
  );
};

export default TwygoUsers;
